import type { CompetencySlug, QuestionType } from '@evaltick/shared';
import { BaseAPIClient } from './base';

// =============================================================================
// TYPES
// =============================================================================

export interface Question {
  id: string;
  competencyId: CompetencySlug;
  questionType: QuestionType;
  text: string;
  createdAt: string;
  updatedAt: string;
}

/** A question set that pins a question — used to warn before edit/delete. */
export interface QuestionSetRef {
  id: string;
  name: string;
}

/** A question plus the question sets that reference it (shape returned by `list`). */
export interface QuestionWithQuestionSets extends Question {
  questionSets: QuestionSetRef[];
}

export interface QuestionPayload {
  competencyId: CompetencySlug;
  questionType: QuestionType;
  text: string;
}

// =============================================================================
// SERVICE
// =============================================================================

export interface QuestionFilters {
  competencyId?: CompetencySlug;
  questionType?: QuestionType;
}

export class QuestionsService extends BaseAPIClient {
  /** Admin: list questions in the bank, optionally filtered by competency and/or type. */
  async list(filters: QuestionFilters = {}): Promise<QuestionWithQuestionSets[]> {
    const params = new URLSearchParams();
    if (filters.competencyId) params.set('competencyId', filters.competencyId);
    if (filters.questionType) params.set('questionType', filters.questionType);
    const query = params.toString();
    return this.get<QuestionWithQuestionSets[]>(`/admin/questions${query ? `?${query}` : ''}`);
  }

  /** Admin: create a question. */
  async create(payload: QuestionPayload): Promise<Question> {
    return this.post<Question>('/admin/questions', payload);
  }

  /** Admin: update a question. */
  async update(id: string, payload: Partial<QuestionPayload>): Promise<Question> {
    return this.patch<Question>(`/admin/questions/${id}`, payload);
  }

  /** Admin: delete a question. */
  async remove(id: string): Promise<void> {
    return this.delete(`/admin/questions/${id}`);
  }
}

export default QuestionsService;
